import React, { ReactElement, memo } from "react";

// components
import { ItemListHeader } from "./components/ItemListHeader";
import { FoodItemWidget } from "@cart-ui/components/food-item";

// types
import { CartItemWithUUID, ItemImagesOnCartXPVariants, ReviewedCartApiResponse } from "@swiggy-private/cart-types";
import { IColors } from "@swiggy-private/dls-web-common/dist/components/types.internal";

// styles
import { Container, ContainerItems, ContainerItemsList } from "./styles";

// helpers
import { getItemsPresentationConfig, isFreebieOptInItem } from "@cart-ui/helpers/Item";

// constants
import { DEAL_RUSH, TOING_LOOT_DEAL } from "@cart-ui/components/food-item/constants";

export interface CartItemListWidgetProps {
    itemListHeader?: {
        title: string;
        subtitle: string;
        subtitleColor: string;
        icon?: React.ReactNode;
        lottieId?: string;
        backgroundColor?: string;
        showAnimation?: boolean;
    };
    items: CartItemWithUUID[];
    onUpdateItem: (item: CartItemWithUUID) => Promise<void>;
    onCustomiseItem: (item: CartItemWithUUID) => Promise<void>;
    updateCartInProgress: boolean;
    setUpdatingItem: (item: CartItemWithUUID | null) => void;
    updatingItem: CartItemWithUUID | null;
    showInputQtyField?: boolean;
    analyticsEvent: {
        onClickItemIncrement: (
            menuItemId: number,
            quantity: number,
            context: string,
            itemGroupTagId?: string | null,
        ) => void;
        onClickItemDecrement: (
            menuItemId: number,
            quantity: number,
            context: string,
            itemGroupTagId?: string | null,
        ) => void;
        onClickRemoveSingleOOSItem: (menuItemId: number, price: number) => void;
        onCustomiseUpdateItem: (menuItemId: number) => void;
        onImpressionNormalCustomisation: (menuItemId: number) => void;
        onImpressionProgressCustomisation: (menuItemId: number) => void;
        onItemImageImpression: (menuItemId: number) => void;
        onImpressionItemListHeader?: (isDealRush: boolean) => void;
    };
    variant: ItemImagesOnCartXPVariants;
    isHost?: boolean;
    isGroupOrderFlow?: boolean;
    isCurrentItemFromSameUser?: boolean;
    menuItemsCalloutPresent?: boolean;
    onDisabledIncrementItemQtyClick: VoidFunction;
    isCouponGroup?: boolean;
    groupTagDetails?: ReviewedCartApiResponse["group_tag_details"];
    isIrctcCartSession?: boolean;
    titleColor?: keyof IColors;
}

function CartItemListWidget({
    items,
    itemListHeader,
    setUpdatingItem,
    updatingItem,
    groupTagDetails,
    titleColor,
    ...props
}: CartItemListWidgetProps): ReactElement {
    const gutterPresent = !!itemListHeader;
    const presentationConfig = getItemsPresentationConfig(groupTagDetails, items[0]);
    // Use titleColor from props if provided, otherwise fall back to presentationConfig or default
    const headerTitleColor = titleColor || presentationConfig?.titleColor;

    // Check if any item in the list is a Deal Rush item
    const isDealRush = items.some((item) => item.item_group_tag_id === DEAL_RUSH);

    const isToingLootDeal = items.some((item) => item.item_group_tag_id === TOING_LOOT_DEAL);

    const handleHeaderImpression = React.useCallback(() => {
        props.analyticsEvent?.onImpressionItemListHeader?.(isDealRush);
    }, [isDealRush, props.analyticsEvent]);

    return (
        <Container
            gutterPresent={gutterPresent}
            menuItemsCalloutPresent={props?.menuItemsCalloutPresent}
            background={presentationConfig?.background}
            isIrctcCartSession={props.isIrctcCartSession}
            isToingLootDeal={isToingLootDeal}
        >
            <ContainerItems>
                {itemListHeader && (
                    <ItemListHeader
                        header={itemListHeader}
                        titleColor={headerTitleColor}
                        onImpression={handleHeaderImpression}
                    />
                )}
                <ContainerItemsList role="list">
                    {items.map((item: CartItemWithUUID, index: number) => {
                        const updating = updatingItem?.unId === item.unId || false;
                        return (
                            <FoodItemWidget
                                key={index}
                                item={item}
                                {...props}
                                updateCartInProgress={props.updateCartInProgress}
                                updating={updating}
                                setUpdating={setUpdatingItem}
                                isFreebieOptInItem={isFreebieOptInItem(item)}
                                isCouponGroup={props.isCouponGroup}
                            />
                        );
                    })}
                </ContainerItemsList>
            </ContainerItems>
        </Container>
    );
}

export default memo(CartItemListWidget);
